import type {
  AdminSkillQuery,
  QuestionDifficulty,
  QuestionStatus,
  QuestionType,
} from '@prepzio/shared';
import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query client + centralized query keys for the admin panel.
 *
 * Mirrors the monorepo convention (mobile uses TanStack Query for all server
 * data): components never fetch/`useEffect` by hand — they read a `useQuery`
 * hook and trigger a `useMutation`, and mutations invalidate the affected keys.
 * Keeping every key here makes invalidation greppable and typo-proof.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Admin data is low-churn; keep it fresh-ish without hammering the API.
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export const adminKeys = {
  // PRE-155: the query is part of the key, so a search does not overwrite the
  // cached page behind it and going back to an earlier page is instant.
  skills: (query: AdminSkillQuery = {}) =>
    [
      'admin',
      'skills',
      query.q ?? '',
      query.origin ?? '',
      query.limit ?? 0,
      query.offset ?? 0,
    ] as const,
  /** Every skills page, whatever its filters — what a mutation invalidates. */
  skillsAll: () => ['admin', 'skills'] as const,
  // PRE-285: the page is part of the key, and the bare prefix stays a valid
  // invalidation target — generating changes every page, not just the one open.
  coverageGaps: (params: { limit?: number; offset?: number } = {}) =>
    ['admin', 'coverage-gaps', params] as const,
  coverageGapsRoot: () => ['admin', 'coverage-gaps'] as const,
  // PRE-293: the models an expert has written.
  itemModels: () => ['admin', 'item-models'] as const,
  bankHealth: () => ['admin', 'bank-health'] as const,
  launchReadiness: () => ['admin', 'launch-readiness'] as const,
  questionFlow: () => ['admin', 'question-flow'] as const,
  reviewStats: () => ['admin', 'review-stats'] as const,
  llmInfo: () => ['admin', 'llm-info'] as const,
  contentSummary: () => ['admin', 'content-summary'] as const,
  roadmaps: () => ['admin', 'roadmaps'] as const,
  roadmapDetail: (id: string) => ['admin', 'roadmaps', id] as const,
  question: (id: string) => ['admin', 'question', id] as const,
  users: () => ['admin', 'users'] as const,
  userDetail: (id: string) => ['admin', 'users', id] as const,
  costs: () => ['admin', 'costs'] as const,
  systemHealth: () => ['admin', 'system-health'] as const,
  appConfig: () => ['admin', 'config'] as const,
  simUsers: () => ['admin', 'sim-users'] as const,

  // BR-90 / PRE-123: skills a CV named that the taxonomy did not have. Separate
  // from `skills` because acting on one changes both lists.
  skillReviewQueue: () => ['admin', 'skill-review-queue'] as const,

  // PRE-188: the matrix.
  rtm: () => ['admin', 'rtm'] as const,

  // The semantic-review suggestion lane: the latest suggestion per requirement,
  // and one in-flight run's progress. Separate keys because the run polls and the
  // suggestion list does not.

  // PRE-190: the product document in D1. Editing either of these changes what
  // the matrix is derived from, so both invalidate `rtm` as well.
  businessRules: () => ['admin', 'business-rules'] as const,
  blueprint: () => ['admin', 'blueprint'] as const,

  promptTemplates: (name?: string) => ['admin', 'prompt-templates', name ?? 'all'] as const,

  drafts: (params: {
    status: QuestionStatus;
    skillId?: string;
    type?: QuestionType;
    difficulty?: QuestionDifficulty;
    limit?: number;
    offset?: number;
  }) => ['admin', 'drafts', params] as const,

  emailTemplates: () => ['admin', 'email-templates'] as const,
  emailTemplate: (id: string) => ['admin', 'email-templates', id] as const,
  emailSends: (params: Record<string, unknown>) => ['admin', 'email-sends', params] as const,
} as const;
